import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Mail, Phone, MapPin, Facebook, Instagram, ArrowUp, Send, Loader2, CheckCircle } from "lucide-react";
import { useSiteCustomization } from "@/context/SiteCustomizationContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
  </svg>
);

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(res.data.message);
      setStatus("success");
      setEmail("");
      setTimeout(() => setStatus("idle"), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Please enter a valid email");
      setStatus("idle");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 max-w-md mx-auto" data-testid="newsletter-form">
      <div className="relative flex-1">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/15 rounded-full text-sm text-white placeholder-gray-500 focus:outline-none focus:border-accent/60 transition-colors"
          data-testid="newsletter-email"
        />
      </div>
      <motion.button
        type="submit"
        disabled={status === "loading" || status === "success"}
        className="px-6 py-3 bg-accent hover:bg-accent/90 disabled:opacity-70 text-white text-sm font-medium rounded-full flex items-center gap-2 transition-colors shrink-0"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        data-testid="newsletter-submit"
      >
        {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> :
         status === "success" ? <><CheckCircle className="w-4 h-4" /> Subscribed</> :
         <><Send className="w-4 h-4" /> Subscribe</>}
      </motion.button>
    </form>
  );
}

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { getContent, isSectionEnabled } = useSiteCustomization();
  const g = (key, fb) => getContent(key, fb);
  const [socialLinks, setSocialLinks] = useState({
    facebook: "#", tiktok: "#", instagram: "#", whatsapp: "#",
  });

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(res => {
      if (res.data?.social_links) setSocialLinks(res.data.social_links);
    }).catch(() => {});
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative" style={{ backgroundColor: 'var(--color-site-footer-bg, #102a43)', color: 'var(--color-site-footer-text, #ffffff)' }} data-testid="site-footer">
      {/* Back to top */}
      <motion.button
        onClick={scrollToTop}
        className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-accent text-white rounded-full flex items-center justify-center shadow-lg shadow-accent/30 hover:bg-accent/90 transition-colors"
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.9 }}
        data-testid="back-to-top-btn"
      >
        <ArrowUp className="w-5 h-5" />
      </motion.button>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <AnimatedSection>
            <Link to="/" className="inline-block mb-6">
              <div className="flex items-center gap-2">
                <img src="/logo.png" alt="Great Messiah Publication" className="h-10 w-10 object-contain" />
                <span className="text-2xl font-bold">{g("footer_brand_name", "Great Messiah Publication")}</span>
              </div>
            </Link>
            <p className="text-gray-400 leading-relaxed mb-6 text-sm">
              {g("footer_brand_desc", "Empowering Ghanaian students with quality BECE preparation materials. GES-aligned mock examinations for academic excellence.")}
            </p>
            <div className="flex gap-3">
              {[
                { Icon: Facebook, key: "facebook" },
                { Icon: TikTokIcon, key: "tiktok" },
                { Icon: Instagram, key: "instagram" },
                { Icon: WhatsAppIcon, key: "whatsapp" },
              ].map(({ Icon, key }) => (
                <motion.a
                  key={key}
                  href={socialLinks[key] || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-accent transition-colors duration-300"
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.9 }}
                  data-testid={`social-${key}`}
                >
                  <Icon className="w-4 h-4" />
                </motion.a>
              ))}
            </div>
          </AnimatedSection>

          {/* Quick Links */}
          <AnimatedSection delay={100}>
            <h3 className="font-semibold text-lg mb-6">Products</h3>
            <ul className="space-y-3">
              {[
                { label: "All Mock Exams", href: "/mock-exams" },
                { label: "Basic 9 (BECE)", href: "/mock-exams?class_level=Basic 9" },
                { label: "Basic 8", href: "/mock-exams?class_level=Basic 8" },
                { label: "Basic 7", href: "/mock-exams?class_level=Basic 7" },
                { label: "Standard Question Bank", href: "/mock-exams?is_bundle=true" },
              ].map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm hover:pl-1"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </AnimatedSection>

          {/* Support */}
          <AnimatedSection delay={200}>
            <h3 className="font-semibold text-lg mb-6">Support</h3>
            <ul className="space-y-3">
              {[
                { label: "About Us", href: "/support/about_us" },
                { label: "Help Center", href: "/support/help_center" },
                { label: "Contact Us", href: "/support/contact_us" },
                ...(isSectionEnabled("for_schools") ? [{ label: "For Schools", href: "/school-services" }] : []),
                ...(isSectionEnabled("testimonials") ? [{ label: "Success Stories", href: "/success-stories" }] : []),
                ...(isSectionEnabled("featured_products") ? [{ label: "Featured Products", href: "/featured-products" }] : []),
                { label: "Refund Policy", href: "/support/refund_policy" },
              ].map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm hover:pl-1"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </AnimatedSection>

          {/* Contact */}
          <AnimatedSection delay={300}>
            <h3 className="font-semibold text-lg mb-6">{g("footer_contact_heading", "Contact Us")}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm whitespace-pre-line">
                  {g("footer_address", "Accra, Ghana\nGreater Accra Region")}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-accent shrink-0" />
                <a href={`tel:${g("footer_phone", "+233 50 123 4567").replace(/\s/g, "")}`} className="text-gray-400 hover:text-white transition-colors text-sm">
                  {g("footer_phone", "+233 50 123 4567")}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-accent shrink-0" />
                <a href={`mailto:${g("footer_email", "info@greatmessiahpub.com")}`} className="text-gray-400 hover:text-white transition-colors text-sm">
                  {g("footer_email", "info@greatmessiahpub.com")}
                </a>
              </li>
            </ul>

            {/* Payment Methods */}
            <div className="mt-8">
              <p className="text-gray-500 text-xs mb-3">Accepted Payment Methods</p>
              <div className="flex flex-wrap gap-2">
                {["MTN MoMo", "Vodafone Cash", "AirtelTigo"].map((method, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-white/10 rounded-full text-xs text-gray-300"
                  >
                    {method}
                  </span>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Newsletter Signup */}
        <AnimatedSection delay={100}>
          <div className="border-t border-white/10 mt-12 pt-10">
            <div className="max-w-xl mx-auto text-center">
              <h3 className="font-semibold text-lg mb-2">{g("footer_newsletter_title", "Stay Updated")}</h3>
              <p className="text-gray-400 text-sm mb-5">{g("footer_newsletter_desc", "Get notified when new mock exams and study materials are released.")}</p>
              <NewsletterForm />
            </div>
          </div>
        </AnimatedSection>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 text-center">
          <p className="text-gray-500 text-sm">
            {g("footer_copyright", "Great Messiah Publication. All rights reserved.")}
          </p>
          <p className="text-gray-600 text-xs mt-2">
            {g("footer_syllabus_note", "Aligned with Ghana Education Service (GES) Syllabus 2024 to date")}
          </p>
        </div>
      </div>
    </footer>
  );
}
